// =============================================================================
// EditAppointmentModal - Modal per modificare/eliminare un appuntamento
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, differenceInMinutes } from 'date-fns';
import { useTenantTimeZone } from '../../store/tenantStore';
import { civilDateInTz, timeStringInTz, wallClockToUtc } from '../../lib/tenantTime';
import { useDateLocale } from '../../hooks/useDateLocale';
import { 
  X, User, Briefcase, Clock, Users, FileText,
  AlertCircle, Loader2, Trash2, Save, Calendar, Plus, ChevronDown,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { 
  useIsEditModalOpen, 
  useSelectedAppointmentId,
  useSchedulerActions,
} from '../../store/schedulerStore';
import {
  useAppointment,
  useMoveAppointment,
  useDeleteAppointment,
  useResources,
  useCustomers,
  useAddParticipant,
  useRemoveParticipant,
} from '../../api/hooks';
import { L } from '../../config/labels';
import type { Appointment, AppointmentStatus, Customer, Resource } from '../../types';

// ===========================================================================
// Status Options
// ===========================================================================

const STATUS_OPTIONS: { value: AppointmentStatus; labelKey: string; color: string }[] = [
  { value: 'PENDING', labelKey: 'appointmentStatus.PENDING', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'CONFIRMED', labelKey: 'appointmentStatus.CONFIRMED', color: 'bg-green-100 text-green-700' },
  { value: 'IN_PROGRESS', labelKey: 'appointmentStatus.IN_PROGRESS', color: 'bg-blue-100 text-blue-700' },
  { value: 'COMPLETED', labelKey: 'appointmentStatus.COMPLETED', color: 'bg-gray-100 text-gray-700' },
  { value: 'CANCELLED', labelKey: 'appointmentStatus.CANCELLED', color: 'bg-red-100 text-red-700' },
  { value: 'NO_SHOW', labelKey: 'appointmentStatus.NO_SHOW', color: 'bg-orange-100 text-orange-700' },
];

// ===========================================================================
// Resource Chip Component
// ===========================================================================

interface ResourceChipProps {
  resource: Resource;
  onRemove: () => void;
  canRemove: boolean;
}

function ResourceChip({ resource, onRemove, canRemove }: ResourceChipProps) {
  const { t } = useTranslation('scheduler');
  return (
    <div 
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full shadow-sm"
    >
      <div 
        className="w-2.5 h-2.5 rounded-full"
        style={{ backgroundColor: resource.color || '#6B7280' }}
      />
      <span className="text-sm font-medium text-gray-800">{resource.name}</span>
      <span className="text-xs text-gray-400">({resource.resourceType?.name})</span>
      {canRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
          title={t('edit.removeResource')}
        >
          <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
        </button>
      )}
    </div>
  );
}

// ===========================================================================
// Resource Selector Dropdown
// ===========================================================================

interface ResourceSelectorProps {
  availableResources: Resource[];
  onSelect: (resourceId: string) => void;
}

function ResourceSelector({ availableResources, onSelect }: ResourceSelectorProps) {
  const { t } = useTranslation('scheduler');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredResources = useMemo(() => {
    if (!search) return availableResources;
    const lowerSearch = search.toLowerCase();
    return availableResources.filter(r => 
      r.name.toLowerCase().includes(lowerSearch) ||
      r.resourceType?.name.toLowerCase().includes(lowerSearch)
    );
  }, [availableResources, search]);

  // Group resources by type
  const groupedResources = useMemo(() => {
    const groups = new Map<string, Resource[]>();
    filteredResources.forEach(r => {
      const typeName = r.resourceType?.name || t('booking.other');
      const current = groups.get(typeName) || [];
      groups.set(typeName, [...current, r]);
    });
    return groups;
  }, [filteredResources]);

  if (availableResources.length === 0) {
    return (
      <div className="text-sm text-gray-400 italic py-2">
        {t('edit.allResourcesAssigned')}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors w-full justify-center"
      >
        <Plus className="w-4 h-4" />
        {t('edit.addResource')}
        <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>
      
      {isOpen && (
        <>
          {/* Backdrop to close */}
          <div 
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('edit.searchResource')}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoFocus
              />
            </div>
            
            {/* Resource List */}
            <div className="overflow-y-auto max-h-48">
              {filteredResources.length === 0 ? (
                <div className="p-4 text-sm text-gray-400 text-center">
                  {t('edit.noResourceFound')}
                </div>
              ) : (
                Array.from(groupedResources.entries()).map(([typeName, resources]) => (
                  <div key={typeName}>
                    <div className="px-3 py-1 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {typeName}
                    </div>
                    {resources.map(resource => (
                      <button
                        key={resource.id}
                        type="button"
                        onClick={() => {
                          onSelect(resource.id);
                          setIsOpen(false);
                          setSearch('');
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-emerald-50 flex items-center gap-2 transition-colors"
                      >
                        <div 
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: resource.color || '#6B7280' }}
                        />
                        <span className="text-sm text-gray-800">{resource.name}</span>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Participants Section — clienti degli eventi di gruppo
// ===========================================================================

interface ParticipantsSectionProps {
  appointment: Appointment;
  maxClients: number;
  onMaxClientsChange: (value: number) => void;
}

function ParticipantsSection({ appointment, maxClients, onMaxClientsChange }: ParticipantsSectionProps) {
  const { t } = useTranslation('scheduler');
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [participantError, setParticipantError] = useState<string | null>(null);

  const { data: customersData, isLoading: customersLoading } = useCustomers(search || undefined);
  const addParticipant = useAddParticipant();
  const removeParticipant = useRemoveParticipant();

  const participants = appointment.participants ?? [];
  const occupancy =
    appointment.participantCount ?? (appointment.customerId ? 1 : 0) + participants.length;
  const isFull = occupancy >= maxClients;

  // Escludi il primario e chi è già iscritto
  const candidates = (customersData?.data ?? []).filter(
    (c: Customer) =>
      c.id !== appointment.customerId && !participants.some(p => p.customerId === c.id),
  );

  const handleAdd = (customerId: string) => {
    setParticipantError(null);
    addParticipant.mutate(
      { appointmentId: appointment.id, customerId },
      {
        onSuccess: () => {
          setShowAdd(false);
          setSearch('');
        },
        onError: (error: any) => {
          const msg = error.response?.data?.message || t('edit.participants.addError');
          setParticipantError(msg);
        },
      },
    );
  };

  const handleRemove = (participantId: string) => {
    setParticipantError(null);
    removeParticipant.mutate(
      { appointmentId: appointment.id, participantId },
      {
        onError: (error: any) => {
          const msg = error.response?.data?.message || t('edit.participants.removeError');
          setParticipantError(msg);
        },
      },
    );
  };

  return (
    <div>
      <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-gray-400" />
        {t('edit.participants.title')}
        <span
          className={cn(
            'ml-auto text-xs font-semibold px-2 py-0.5 rounded-full',
            isFull ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700',
          )}
        >
          {occupancy}/{maxClients}
        </span>
      </label>

      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
        {/* Cliente primario (non rimovibile) */}
        {appointment.customerName && (
          <div className="flex items-center justify-between py-1">
            <span className="text-sm font-medium text-gray-900">{appointment.customerName}</span>
            <span className="text-xs text-gray-400 font-medium">
              {t('edit.participants.primary')}
            </span>
          </div>
        )}

        {/* Partecipanti aggiuntivi */}
        {participants.map(p => (
          <div key={p.id} className="flex items-center justify-between py-1 border-t border-gray-100">
            <span className="text-sm text-gray-800">{p.customerName}</span>
            <button
              type="button"
              onClick={() => handleRemove(p.id)}
              disabled={removeParticipant.isPending}
              className="p-1 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
              title={t('edit.participants.remove')}
            >
              <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
            </button>
          </div>
        ))}

        {/* Aggiungi partecipante */}
        {!isFull && !showAdd && (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('edit.participants.add')}
          </button>
        )}
        {isFull && (
          <p className="text-xs text-amber-600">{t('edit.participants.full')}</p>
        )}

        {showAdd && (
          <div className="space-y-1.5">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('edit.participants.searchPlaceholder')}
              autoFocus
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <div className="border border-gray-200 rounded-lg bg-white max-h-32 overflow-y-auto">
              {customersLoading ? (
                <div className="p-2 text-center text-xs text-gray-400">{t('common:status.loading')}</div>
              ) : candidates.length === 0 ? (
                <div className="p-2 text-center text-xs text-gray-400">
                  {t('edit.participants.noCandidates')}
                </div>
              ) : (
                candidates.map((customer: Customer) => (
                  <button
                    key={customer.id}
                    type="button"
                    onClick={() => handleAdd(customer.id)}
                    disabled={addParticipant.isPending}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-emerald-50 border-b border-gray-100 last:border-0 disabled:opacity-50"
                  >
                    {customer.firstName} {customer.lastName}
                  </button>
                ))
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setShowAdd(false);
                setSearch('');
              }}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              {t('common:buttons.cancel')}
            </button>
          </div>
        )}

        {participantError && (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {participantError}
          </p>
        )}

        {/* Capienza massima (salvata con "Salva modifiche") */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div>
            <span className="text-sm text-gray-700">{t('edit.participants.maxClients')}</span>
            {appointment.serviceMaxClients != null &&
              appointment.serviceMaxClients !== maxClients && (
                <p className="text-xs text-gray-400">
                  {t('booking.maxClientsDefaultHint', { value: appointment.serviceMaxClients })}
                </p>
              )}
          </div>
          <input
            type="number"
            min={Math.max(1, occupancy)}
            max={500}
            value={maxClients}
            onChange={e => onMaxClientsChange(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Main Component
// ===========================================================================

/**
 * Wrapper: monta il contenuto — e quindi le sue query — solo a modale aperta.
 *
 * Il componente è montato globalmente in App.tsx, fuori dalle Routes: senza
 * questo guard gli hook interni partirebbero su ogni route, incluse /login e
 * /select-tenant, dove non c’è un tenant attivo e le chiamate tornano 403.
 */
export function EditAppointmentModal() {
  const isOpen = useIsEditModalOpen();
  if (!isOpen) return null;
  return <EditAppointmentModalContent />;
}

function EditAppointmentModalContent() {
  const { t } = useTranslation('scheduler');
  const dateLocale = useDateLocale();
  const timeZone = useTenantTimeZone();
  const appointmentId = useSelectedAppointmentId();
  const { closeEditModal } = useSchedulerActions();
  
  // Form State
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<AppointmentStatus>('PENDING');
  const [startTime, setStartTime] = useState('');
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [maxClients, setMaxClients] = useState(1);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // API Hooks
  const { data: appointment, isLoading, isError } = useAppointment(appointmentId || '');
  const { data: allResources = [] } = useResources();
  const moveAppointment = useMoveAppointment();
  const deleteAppointment = useDeleteAppointment();

  // Derived values
  const duration = useMemo(() => {
    if (!appointment) return 0;
    const start = parseISO(appointment.scheduledStart);
    const end = parseISO(appointment.scheduledEnd);
    return differenceInMinutes(end, start);
  }, [appointment]);

  // Get full Resource objects for selected IDs
  const selectedResources = useMemo(() => {
    return selectedResourceIds
      .map(id => allResources.find(r => r.id === id))
      .filter((r): r is Resource => r !== undefined);
  }, [selectedResourceIds, allResources]);

  // Available resources (not already assigned)
  const availableResources = useMemo(() => {
    return allResources.filter(r => !selectedResourceIds.includes(r.id));
  }, [allResources, selectedResourceIds]);

  // Initialize form when appointment loads
  useEffect(() => {
    if (appointment) {
      setNotes(appointment.notes || '');
      setStatus(appointment.status);
      setStartTime(timeStringInTz(parseISO(appointment.scheduledStart), timeZone));
      setSelectedResourceIds(appointment.allocations?.map(a => a.resourceId) || []);
      setMaxClients(appointment.maxClients ?? 1);
      setHasChanges(false);
      setSubmitError(null);
      setShowDeleteConfirm(false);
    }
  }, [appointment]);

  // Track changes
  useEffect(() => {
    if (!appointment) return;
    
    const originalNotes = appointment.notes || '';
    const originalStatus = appointment.status;
    const originalTime = timeStringInTz(parseISO(appointment.scheduledStart), timeZone);
    const originalResourceIds = appointment.allocations?.map(a => a.resourceId) || [];
    
    // Check if resources changed (order doesn't matter)
    const resourcesChanged = 
      selectedResourceIds.length !== originalResourceIds.length ||
      !selectedResourceIds.every(id => originalResourceIds.includes(id));
    
    const changed =
      notes !== originalNotes ||
      status !== originalStatus ||
      startTime !== originalTime ||
      resourcesChanged ||
      maxClients !== (appointment.maxClients ?? 1);

    setHasChanges(changed);
  }, [appointment, notes, status, startTime, selectedResourceIds, maxClients]);

  // Handlers
  const handleClose = () => {
    closeEditModal();
    setSubmitError(null);
    setShowDeleteConfirm(false);
    moveAppointment.reset();
    deleteAppointment.reset();
  };

  const handleRemoveResource = (resourceId: string) => {
    setSelectedResourceIds(prev => prev.filter(id => id !== resourceId));
  };

  const handleAddResource = (resourceId: string) => {
    setSelectedResourceIds(prev => [...prev, resourceId]);
  };

  const handleSave = () => {
    if (!appointment || !hasChanges) return;
    
    // Validate at least one resource
    if (selectedResourceIds.length === 0) {
      setSubmitError(t('edit.validationNoResources'));
      return;
    }
    
    setSubmitError(null);

    // Build update payload
    const updateData: {
      id: string;
      notes?: string;
      status?: string;
      startTime?: string;
      resourceIds?: string[];
      maxClients?: number;
    } = { id: appointment.id };

    // Check what changed
    const originalNotes = appointment.notes || '';
    const originalStatus = appointment.status;
    const originalTime = timeStringInTz(parseISO(appointment.scheduledStart), timeZone);
    const originalResourceIds = appointment.allocations?.map(a => a.resourceId) || [];

    if (notes !== originalNotes) {
      updateData.notes = notes;
    }
    if (status !== originalStatus) {
      updateData.status = status;
    }
    if (startTime !== originalTime) {
      // Il nuovo orario e' un orario di parete dell'organizzazione, da applicare
      // al giorno civile su cui l'appuntamento cade nel SUO fuso.
      const originalDay = civilDateInTz(parseISO(appointment.scheduledStart), timeZone);
      const [hours, minutes] = startTime.split(':').map(Number);
      updateData.startTime = wallClockToUtc(
        originalDay,
        hours * 60 + minutes,
        timeZone,
      ).toISOString();
    }
    
    // Check if resources changed
    const resourcesChanged = 
      selectedResourceIds.length !== originalResourceIds.length ||
      !selectedResourceIds.every(id => originalResourceIds.includes(id));
    
    if (resourcesChanged) {
      updateData.resourceIds = selectedResourceIds;
    }

    if (maxClients !== (appointment.maxClients ?? 1)) {
      updateData.maxClients = maxClients;
    }

    moveAppointment.mutate(updateData, {
      onSuccess: () => {
        handleClose();
      },
      onError: (error: any) => {
        console.error('Failed to update appointment:', error);
        const msg = error.response?.data?.message || t('edit.saveError');
        setSubmitError(msg);
      },
    });
  };

  const handleDelete = () => {
    if (!appointment) return;
    
    deleteAppointment.mutate(appointment.id, {
      onSuccess: () => {
        handleClose();
      },
      onError: (error: any) => {
        console.error('Failed to delete appointment:', error);
        const msg = error.response?.data?.message || t('edit.deleteError');
        setSubmitError(msg);
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Calendar className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('edit.title', { entity: L.appointment })}
              </h2>
              <p className="text-sm text-gray-500">
                {t('edit.subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              <span className="ml-3 text-gray-500">{t('common:status.loading')}</span>
            </div>
          ) : isError || !appointment ? (
            <div className="flex items-center justify-center py-12 text-red-500">
              <AlertCircle className="w-6 h-6 mr-2" />
              <span>{t('edit.loadError')}</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cliente (read-only) — per gli eventi di gruppo vedi sezione Partecipanti */}
              {(appointment.maxClients ?? 1) <= 1 && (appointment.participants?.length ?? 0) === 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-gray-400" />
                    {L.customer}
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="font-medium text-gray-900">
                      {appointment.customerName || t('internalActivity')}
                    </span>
                    {appointment.customerEmail && (
                      <span className="text-sm text-gray-500 ml-2">({appointment.customerEmail})</span>
                    )}
                  </div>
                </div>
              )}

              {/* Partecipanti (eventi di gruppo) */}
              {((appointment.maxClients ?? 1) > 1 || (appointment.participants?.length ?? 0) > 0) && (
                <ParticipantsSection
                  appointment={appointment}
                  maxClients={maxClients}
                  onMaxClientsChange={setMaxClients}
                />
              )}

              {/* Servizio (read-only) */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  {L.service}
                </label>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3">
                  {appointment.serviceColor && (
                    <div 
                      className="w-4 h-4 rounded-full border border-white shadow-sm"
                      style={{ backgroundColor: appointment.serviceColor }}
                    />
                  )}
                  <span className="font-medium text-gray-900">{appointment.serviceName}</span>
                  <span className="text-sm text-gray-500">({duration} min)</span>
                </div>
              </div>

              {/* Data e Orario */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {t('edit.dateTime')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="text-sm text-gray-500">{t('common:fields.date')}</span>
                    <p className="font-medium text-gray-900">
                      {format(parseISO(appointment.scheduledStart), 'EEEE d MMMM yyyy', { locale: dateLocale })}
                    </p>
                  </div>
                  <div>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-900 font-medium"
                    />
                  </div>
                </div>
              </div>

              {/* Risorse Assegnate (EDITABLE) */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  {t('edit.assignedResources')}
                </label>
                
                {/* Current Resources as Chips */}
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-gray-50 rounded-lg border border-gray-200">
                    {selectedResources.length === 0 ? (
                      <span className="text-sm text-gray-400 italic">{t('edit.noResources')}</span>
                    ) : (
                      selectedResources.map(resource => (
                        <ResourceChip
                          key={resource.id}
                          resource={resource}
                          onRemove={() => handleRemoveResource(resource.id)}
                          canRemove={selectedResources.length > 1}
                        />
                      ))
                    )}
                  </div>
                  
                  {/* Add Resource Dropdown */}
                  <ResourceSelector
                    availableResources={availableResources}
                    onSelect={handleAddResource}
                  />
                </div>
              </div>

              {/* Stato */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  {t('common:fields.status')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStatus(opt.value)}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                        status === opt.value
                          ? `${opt.color} ring-2 ring-offset-1 ring-current`
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  {t('common:fields.notes')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('edit.notesPlaceholder')}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">{t('edit.error')}</p>
                    <p className="text-sm text-red-600 mt-1">{submitError}</p>
                  </div>
                </div>
              )}

              {/* Delete Confirmation */}
              {showDeleteConfirm && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-3">
                    {t('edit.deleteConfirm', { entity: L.appointment.toLowerCase() })}
                  </p>
                  <p className="text-sm text-red-600 mb-4">
                    {t('edit.deleteWarning')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDelete}
                      disabled={deleteAppointment.isPending}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {deleteAppointment.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      {t('edit.confirmDelete')}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="btn-secondary px-4 py-2"
                    >
                      {t('common:buttons.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isLoading && !isError && appointment && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            {/* Delete Button */}
            {!showDeleteConfirm && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                {t('common:buttons.delete')}
              </button>
            )}
            {showDeleteConfirm && <div />}

            {/* Save/Cancel Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges || moveAppointment.isPending}
                className={cn(
                  'flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all',
                  hasChanges
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-200'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                )}
              >
                {moveAppointment.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {t('common:buttons.saveChanges')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
